// AdminServiceability.jsx
// ----------------------------------------------------------------------
// Admin dashboard for managing the pickup-serviceability feature.
// Three tabs:
//   1) Hubs           — edit service_radius_km / supported_pincodes /
//                        is_active / is_pickup_enabled  (per hub row)
//   2) Unsupported    — table of "Notify me" leads. Filter + CSV export +
//                        mark-as-notified action
//   3) Analytics      — counts, recharts bars + line, Leaflet map of
//                        unsupported request locations
//
// Backend endpoints used (all behind /api):
//   GET    /admin/serviceability/hubs
//   PATCH  /admin/serviceability/hubs/:id
//   GET    /admin/serviceability/unsupported-requests
//   GET    /admin/serviceability/unsupported-requests.csv
//   POST   /admin/serviceability/unsupported-requests/:id/mark-notified
//   GET    /admin/serviceability/analytics
import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../services/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Loader2, Pencil, Download, BellRing, MapPin, X, Plus, CheckCircle2, AlertCircle, RotateCcw } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Legend } from "recharts";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";

const PINCODE_RE = /^[1-9][0-9]{5}$/;

// ──────────────────────────────────────────────────────────────────────
// Reusable: 6-digit pincode chip-input  (tag-style validation on add)
// ──────────────────────────────────────────────────────────────────────
function PincodeChipInput({ value, onChange }) {
  const [input, setInput] = useState("");
  const inputRef = useRef(null);

  const add = (raw) => {
    const p = (raw || "").trim();
    if (!p) return;
    if (!PINCODE_RE.test(p)) {
      toast.error("Pincode must be 6 digits starting 1-9 (e.g. 560001).");
      return;
    }
    if (value.includes(p)) {
      toast.message("Pincode already added.");
      return;
    }
    onChange([...value, p]);
    setInput("");
  };

  const remove = (p) => onChange(value.filter((x) => x !== p));

  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add(input);
    } else if (e.key === "Backspace" && !input && value.length) {
      remove(value[value.length - 1]);
    }
  };

  return (
    <div className="w-full">
      <div
        className="flex flex-wrap gap-1.5 rounded-md border border-input bg-background p-2 min-h-[42px] focus-within:ring-2 focus-within:ring-orange-400"
        onClick={() => inputRef.current?.focus()}
        data-testid="pincode-chip-input"
      >
        {value.map((p) => (
          <Badge
            key={p}
            variant="secondary"
            className="bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100"
            data-testid={`pincode-chip-${p}`}
          >
            {p}
            <button
              type="button"
              className="ml-1 hover:text-orange-900"
              onClick={(e) => { e.stopPropagation(); remove(p); }}
              aria-label={`Remove ${p}`}
              data-testid={`pincode-chip-remove-${p}`}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        <input
          ref={inputRef}
          className="flex-1 min-w-[110px] outline-none bg-transparent text-sm"
          value={input}
          onChange={(e) => setInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
          onKeyDown={handleKey}
          onBlur={() => input && add(input)}
          placeholder="Type 6-digit pin, Enter"
          inputMode="numeric"
          data-testid="pincode-chip-new-input"
        />
      </div>
      <p className="text-[11px] text-muted-foreground mt-1">Press Enter or comma to add. Backspace removes last.</p>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Tab 1: Hubs Management
// ──────────────────────────────────────────────────────────────────────
function HubsTab() {
  const [hubs, setHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ service_radius_km: 25, supported_pincodes: [], is_active: true, is_pickup_enabled: true });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/serviceability/hubs");
      setHubs(data.hubs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load hubs");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openEdit = (h) => {
    setEditing(h);
    setForm({
      service_radius_km: Number(h.service_radius_km ?? 25),
      supported_pincodes: Array.isArray(h.supported_pincodes) ? [...h.supported_pincodes] : [],
      is_active: !!h.is_active,
      is_pickup_enabled: h.is_pickup_enabled !== false,
    });
  };

  const save = async () => {
    if (!editing) return;
    const id = editing.hub_id || editing.id;
    setSaving(true);
    try {
      await api.patch(`/admin/serviceability/hubs/${id}`, {
        service_radius_km: Number(form.service_radius_km) || 25,
        supported_pincodes: form.supported_pincodes,
        is_active: form.is_active,
        is_pickup_enabled: form.is_pickup_enabled,
      });
      toast.success(`Updated ${editing.name}`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save hub");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="hubs-tab-content">
      <Card>
        <CardHeader>
          <CardTitle>Hubs · service area configuration</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading hubs…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground border-b">
                  <tr>
                    <th className="py-2 pr-3">Hub</th>
                    <th className="py-2 pr-3">City</th>
                    <th className="py-2 pr-3">Radius (km)</th>
                    <th className="py-2 pr-3">Supported pincodes</th>
                    <th className="py-2 pr-3">Active</th>
                    <th className="py-2 pr-3">Pickup enabled</th>
                    <th className="py-2 pr-3" />
                  </tr>
                </thead>
                <tbody>
                  {hubs.map((h) => (
                    <tr key={h.hub_id || h.id} className="border-b last:border-b-0" data-testid={`hub-row-${h.hub_id || h.id}`}>
                      <td className="py-2 pr-3 font-medium">{h.name || h.hub_id}</td>
                      <td className="py-2 pr-3">{h.city || "—"}</td>
                      <td className="py-2 pr-3">{h.service_radius_km ?? 25}</td>
                      <td className="py-2 pr-3">
                        {(h.supported_pincodes || []).length > 0 ? (
                          <div className="flex flex-wrap gap-1 max-w-md">
                            {(h.supported_pincodes || []).slice(0, 6).map((p) => (
                              <Badge key={p} variant="outline" className="text-[10px]">{p}</Badge>
                            ))}
                            {h.supported_pincodes.length > 6 && (
                              <span className="text-[10px] text-muted-foreground">+{h.supported_pincodes.length - 6} more</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        {h.is_active ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Active</Badge> : <Badge variant="secondary">Off</Badge>}
                      </td>
                      <td className="py-2 pr-3">
                        {h.is_pickup_enabled !== false ? <Badge className="bg-blue-100 text-blue-700 border-blue-200">Pickup ON</Badge> : <Badge variant="secondary">Pickup OFF</Badge>}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        <Button size="sm" variant="outline" onClick={() => openEdit(h)} data-testid={`hub-edit-${h.hub_id || h.id}`}>
                          <Pencil className="h-3 w-3 mr-1" /> Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {hubs.length === 0 && (
                <p className="text-sm text-muted-foreground py-6 text-center">No hubs found.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit hub · {editing?.name}</DialogTitle>
            <DialogDescription>Adjust the service radius and pincode override list. Changes take effect immediately on new booking attempts.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="radius">Service radius (km)</Label>
                <Input id="radius" type="number" min="0.5" max="500" step="0.5" value={form.service_radius_km} onChange={(e) => setForm({ ...form, service_radius_km: e.target.value })} data-testid="hub-edit-radius" />
              </div>
              <div>
                <Label>Supported pincodes (override list)</Label>
                <PincodeChipInput value={form.supported_pincodes} onChange={(v) => setForm({ ...form, supported_pincodes: v })} />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <Label className="text-sm">Hub active</Label>
                  <p className="text-xs text-muted-foreground">If off, no pickups are routed to this hub.</p>
                </div>
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="hub-edit-active" />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <Label className="text-sm">Pickup enabled</Label>
                  <p className="text-xs text-muted-foreground">If off, hub is active but won't accept new pickups (drop-off only).</p>
                </div>
                <Switch checked={form.is_pickup_enabled} onCheckedChange={(v) => setForm({ ...form, is_pickup_enabled: v })} data-testid="hub-edit-pickup-enabled" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="hub-edit-save">
              {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving…</> : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Tab 2: Unsupported Requests
// ──────────────────────────────────────────────────────────────────────
function UnsupportedTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ city: "", pincode: "" });
  const [actioning, setActioning] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.city) params.city = filters.city;
      if (filters.pincode) params.pincode = filters.pincode;
      const { data } = await api.get("/admin/serviceability/unsupported-requests", { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load requests");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const markNotified = async (id) => {
    setActioning(id);
    try {
      await api.post(`/admin/serviceability/unsupported-requests/${id}/mark-notified`);
      toast.success("Marked as notified");
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, notified: true, notified_at: new Date().toISOString() } : r)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark");
    } finally {
      setActioning(null);
    }
  };

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem("pickngo_token");
      const base = api.defaults.baseURL || "";
      const params = new URLSearchParams();
      if (filters.city) params.set("city", filters.city);
      if (filters.pincode) params.set("pincode", filters.pincode);
      const url = `${base}/admin/serviceability/unsupported-requests.csv${params.toString() ? "?" + params.toString() : ""}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = "unsupported_requests.csv";
      a.click();
      URL.revokeObjectURL(u);
    } catch (e) {
      toast.error("Failed to download CSV");
    }
  };

  return (
    <div data-testid="unsupported-tab-content">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Unsupported pickup requests ({total})</CardTitle>
          <div className="flex items-center gap-2">
            <Input className="h-8 w-32" placeholder="City filter" value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} data-testid="unsupported-filter-city" />
            <Input className="h-8 w-28" placeholder="Pincode" inputMode="numeric" value={filters.pincode} onChange={(e) => setFilters({ ...filters, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })} data-testid="unsupported-filter-pincode" />
            <Button size="sm" onClick={load} variant="outline" data-testid="unsupported-apply-filters"><RotateCcw className="h-3.5 w-3.5 mr-1" /> Apply</Button>
            <Button size="sm" onClick={downloadCsv} variant="outline" data-testid="unsupported-csv-btn"><Download className="h-3.5 w-3.5 mr-1" /> CSV</Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No requests match the filter.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground border-b">
                  <tr>
                    <th className="py-2 pr-3">Requested at</th>
                    <th className="py-2 pr-3">Name</th>
                    <th className="py-2 pr-3">Phone</th>
                    <th className="py-2 pr-3">Email</th>
                    <th className="py-2 pr-3">City</th>
                    <th className="py-2 pr-3">Pincode</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b last:border-b-0" data-testid={`unsupported-row-${r.id}`}>
                      <td className="py-2 pr-3 text-xs">{(r.requested_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="py-2 pr-3 font-medium">{r.name}</td>
                      <td className="py-2 pr-3">{r.phone}</td>
                      <td className="py-2 pr-3 text-xs">{r.email || "—"}</td>
                      <td className="py-2 pr-3">{r.requested_city || "—"}</td>
                      <td className="py-2 pr-3">{r.pickup_pincode || "—"}</td>
                      <td className="py-2 pr-3">
                        {r.notified
                          ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200"><CheckCircle2 className="h-3 w-3 mr-1" /> Notified</Badge>
                          : <Badge variant="outline"><BellRing className="h-3 w-3 mr-1" /> Pending</Badge>}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        {!r.notified && (
                          <Button size="sm" variant="outline" disabled={actioning === r.id} onClick={() => markNotified(r.id)} data-testid={`unsupported-mark-${r.id}`}>
                            {actioning === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Mark notified"}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Tab 3: Analytics
// ──────────────────────────────────────────────────────────────────────
function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/serviceability/analytics");
        setData(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load analytics");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Compute map center + bounds from heatmap or fallback to India.
  const mapCenter = useMemo(() => {
    const pts = data?.heatmap_points || [];
    if (pts.length === 0) return [22.5, 80]; // India centroid-ish
    const avgLat = pts.reduce((s, p) => s + p.lat, 0) / pts.length;
    const avgLng = pts.reduce((s, p) => s + p.lng, 0) / pts.length;
    return [avgLat, avgLng];
  }, [data]);

  if (loading) {
    return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading analytics…</div>;
  }
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="analytics-tab-content">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card>
          <CardHeader><CardTitle className="text-sm">Requests (last 30d)</CardTitle></CardHeader>
          <CardContent><p className="text-3xl font-bold text-orange-600" data-testid="kpi-requests-30d">{data.requests_last_30d ?? 0}</p></CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-sm">Cities asking</CardTitle></CardHeader>
          <CardContent><p className="text-3xl font-bold text-orange-600">{(data.top_unsupported_cities || []).length}</p></CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-sm">Unique pincodes</CardTitle></CardHeader>
          <CardContent><p className="text-3xl font-bold text-orange-600">{(data.top_unsupported_pincodes || []).length}</p></CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle>Top unsupported cities</CardTitle></CardHeader>
          <CardContent>
            <div style={{ width: "100%", height: 260 }} data-testid="chart-cities">
              <ResponsiveContainer>
                <BarChart data={data.top_unsupported_cities || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="city" interval={0} angle={-15} textAnchor="end" height={50} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#f97316" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Top unsupported pincodes</CardTitle></CardHeader>
          <CardContent>
            <div style={{ width: "100%", height: 260 }} data-testid="chart-pincodes">
              <ResponsiveContainer>
                <BarChart data={data.top_unsupported_pincodes || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="pincode" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0ea5e9" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle>30-day request trend</CardTitle></CardHeader>
        <CardContent>
          <div style={{ width: "100%", height: 260 }} data-testid="chart-trend">
            <ResponsiveContainer>
              <LineChart data={data.trend_30d || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="count" stroke="#f97316" strokeWidth={2} dot={false} name="Requests" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><MapPin className="h-4 w-4 text-orange-500" /> Unsupported request locations</CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ height: 380, width: "100%" }} className="rounded-md overflow-hidden border" data-testid="analytics-map">
            <MapContainer center={mapCenter} zoom={data.heatmap_points?.length ? 5 : 4} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
              <TileLayer
                attribution='&copy; OpenStreetMap'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {(data.heatmap_points || []).map((p, i) => (
                <CircleMarker
                  key={i}
                  center={[p.lat, p.lng]}
                  radius={Math.min(20, 6 + Math.log2(p.count + 1) * 4)}
                  pathOptions={{ color: "#dc2626", fillColor: "#f97316", fillOpacity: 0.5, weight: 1 }}
                >
                  <Popup>{p.count} request{p.count > 1 ? "s" : ""} near ({p.lat.toFixed(2)}, {p.lng.toFixed(2)})</Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
          {(data.heatmap_points || []).length === 0 && (
            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> No geolocated requests yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Page root
// ──────────────────────────────────────────────────────────────────────
export default function AdminServiceability() {
  return (
    <div className="space-y-4" data-testid="admin-serviceability-page">
      <div>
        <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">Serviceability</h1>
        <p className="text-sm text-slate-500 mt-1">Manage hub service areas, view "notify me" leads, and analyse unsupported demand.</p>
      </div>
      <Tabs defaultValue="hubs">
        <TabsList data-testid="serviceability-tabs">
          <TabsTrigger value="hubs" data-testid="tab-hubs"><MapPin className="h-3.5 w-3.5 mr-1.5" /> Hubs</TabsTrigger>
          <TabsTrigger value="unsupported" data-testid="tab-unsupported"><BellRing className="h-3.5 w-3.5 mr-1.5" /> Unsupported requests</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics"><Plus className="h-3.5 w-3.5 mr-1.5 rotate-45" /> Analytics</TabsTrigger>
        </TabsList>
        <TabsContent value="hubs"><HubsTab /></TabsContent>
        <TabsContent value="unsupported"><UnsupportedTab /></TabsContent>
        <TabsContent value="analytics"><AnalyticsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
